import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/hooks'
import { verifyAuth } from '@/store/authSlice'
import { SnackbarType } from 'utils/snackbar'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { buildTmsTruckSeatLimitPrompt, type TmsTruckSeatLimitPrompt } from '@/features/billing/tms/overagePrice'
import type { ClaimDriverSubmit } from '../components/ClaimDriverModal'
import { createBoxTruckAssetForDriver } from '../services/createBoxTruckAssetForDriver'
import { fetchBoxTruckDriverProfileOrNull } from '../services/boxTruckProfilesApi'
import { fillBlankTruckFields, truckBlockedReason, truckDraftFromVerification, type TruckDraft } from '../utils/truckFromVerification'
import type { BoxTruckVerification } from '../services/boxTruckLoadsApi'

interface UseClaimDriverWithTruckArgs {
    claimDriver: (row: BoxTruckVerification, leadSource?: string) => Promise<{ driverId?: string }>
    showSnackbar: (type: SnackbarType, message: string) => void
    onFinished: () => void
}

/**
 * Adding a driver from Driver Verifications, and the vehicle they described with them.
 *
 * A state machine, not page code: claim, hydrate the truck from the record the claim just made readable,
 * create it under the seat guard, answer a billing prompt, and report which of the four writes did not
 * land. It lives here so the page stays wiring and so each of those branches can be exercised on its own.
 */
export const useClaimDriverWithTruck = ({ claimDriver, showSnackbar, onFinished }: UseClaimDriverWithTruckArgs) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [claimTarget, setClaimTarget] = useState<BoxTruckVerification | null>(null)
    const [claiming, setClaiming] = useState(false)
    const [seatPrompt, setSeatPrompt] = useState<TmsTruckSeatLimitPrompt | null>(null)
    const pendingTruck = useRef<{ truck: TruckDraft; driverId?: string; name: string } | null>(null)

    const closeClaimModal = () => {
        setClaimTarget(null)
        onFinished()
    }

    /**
     * Create the vehicle for a driver who has already been claimed.
     *
     * Returns false when a seat refusal took over — the same 402/409 prompt Assets shows, because this is
     * the same `POST /trucks` guard and an operator who is one truck over their plan should be asked to
     * confirm the charge, not told the truck "could not be created" with the reason buried in a snackbar.
     */
    const addTruckForClaimedDriver = async (truck: TruckDraft, driverId: string | undefined, name: string, acceptOverage = false): Promise<boolean> => {
        try {
            const outcome = await createBoxTruckAssetForDriver({ truck, driverId, acceptOverage })
            // Re-read the application BEFORE leaving. The refresh after the claim ran while this truck did not
            // exist yet, and the driver page reads its Equipment section out of the store — so the vehicle was
            // missing until something else happened to refresh it, which is why it appeared only after the
            // operator had walked in and out of the driver two or three times.
            await dispatch(verifyAuth())
            // The truck EXISTS in every one of these. Name what is missing from it and where to finish the
            // job — never "the truck could not be created", which sends the operator to create a second one.
            const missing = [
                outcome.detailsFailed ? 'its details' : '',
                outcome.assignmentFailed ? 'the driver assignment' : '',
                outcome.documentsFailed ? 'their vehicle documents' : '',
            ].filter(Boolean)
            if (missing.length > 0) {
                showSnackbar(SnackbarType.WARNING, `${name} added and the truck created, but ${missing.join(', ')} did not save. Finish it in Assets.`)
            } else {
                showSnackbar(SnackbarType.SUCCESS, `${name} added to Drivers with their truck.`)
            }
        } catch (e) {
            const prompt = await buildTmsTruckSeatLimitPrompt(e, {
                activeTitle: 'Extra truck — billing update',
                activeActionLabel: 'Add & update billing',
            })
            if (prompt) {
                pendingTruck.current = { truck, driverId, name }
                setSeatPrompt(prompt)
                return false
            }
            showSnackbar(
                SnackbarType.WARNING,
                `${name} was added, but the truck could not be created: ${getUserErrorMessage(e) || 'please add it from their profile.'}`
            )
        }
        return true
    }

    /**
     * The vehicle as the DRIVER described it, under whatever the operator typed over it.
     *
     * `lookupFailed` matters: the truck is judged complete or not straight after this, and a record that
     * could not be READ looks exactly like a record with nothing in it. Told apart, an outage says so;
     * conflated, an operator was sent to type a VIN their driver had already given us.
     */
    const completeTruckFromClaimedDriver = async (truck: TruckDraft, driverId: string | undefined): Promise<{ truck: TruckDraft; lookupFailed: boolean }> => {
        if (!driverId) return { truck, lookupFailed: false }
        try {
            const full = await fetchBoxTruckDriverProfileOrNull(driverId)
            return { truck: full ? fillBlankTruckFields(truck, truckDraftFromVerification(full.registration)) : truck, lookupFailed: false }
        } catch (error) {
            console.error('[box-truck] could not read the claimed driver’s record to complete their truck', error)
            return { truck, lookupFailed: true }
        }
    }

    /** The operator accepted the extra truck on their bill: re-send the vehicle alone. */
    const confirmSeatOverage = async () => {
        const pending = pendingTruck.current
        if (!pending) return
        setClaiming(true)
        const settled = await addTruckForClaimedDriver(pending.truck, pending.driverId, pending.name, true)
        setClaiming(false)
        if (!settled) return
        setSeatPrompt(null)
        pendingTruck.current = null
        navigate('/profiles/drivers')
    }

    /** Declined or dismissed. The driver is in Drivers either way; say so rather than leaving it silent. */
    const dismissSeatPrompt = () => {
        const pending = pendingTruck.current
        setSeatPrompt(null)
        pendingTruck.current = null
        if (pending) showSnackbar(SnackbarType.WARNING, `${pending.name} was added without a truck. You can add it from their profile.`)
        navigate('/profiles/drivers')
    }

    /**
     * Add the driver, then the vehicle they described.
     *
     * In that order and not in one transaction, deliberately. The driver is the thing the operator asked for
     * and the only step that cannot be repeated later; the truck can be added from the driver's own Equipment
     * section afterwards. So a refused truck — a seat limit, a duplicate VIN — leaves a claimed driver and an
     * explicit message, rather than throwing away a claim that had already succeeded.
     */
    const confirmClaim = async ({ leadSource: source, truck }: ClaimDriverSubmit) => {
        if (!claimTarget) return
        setClaiming(true)
        const name = claimTarget.boxTruckInfo.info?.name || claimTarget.name || 'Driver'
        let driverId: string | undefined
        try {
            driverId = (await claimDriver(claimTarget, source)).driverId
            await dispatch(verifyAuth())
        } catch (e) {
            // Surface the backend's specific 409 reason (e.g. already claimed by another operator) instead of a generic message.
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Couldn’t add the driver. Please try again.')
            setClaiming(false)
            return
        }

        if (truck) {
            // Only NOW is the driver's own record readable in full. The Verifications queue is CROSS-TENANT,
            // so for a driver nobody has claimed it exposes name, phone, equipment and the carrier numbers and
            // withholds the rest — the VIN, truck number, make, model and cargo box they submitted included.
            // The modal therefore could not offer them; the claim is what makes them ours to read. Whatever
            // the operator typed still wins, this only fills what they left blank, and a failure here leaves
            // the form's own values to be saved as they are.
            const { truck: complete, lookupFailed } = await completeTruckFromClaimedDriver(truck, driverId)
            // Checked HERE, not in the modal: the queue withholds an unclaimed driver's VIN, so the form can
            // only ever show half a vehicle — it is the driver's own record, readable once they are ours,
            // that fills the rest in. Still short of what `POST /trucks` requires, the driver stands and the
            // vehicle is left for Assets, which is the recoverable half of this.
            const blocked = truckBlockedReason(complete, lookupFailed)
            if (blocked) {
                showSnackbar(
                    SnackbarType.WARNING,
                    blocked === 'lookup-failed'
                        ? `${name} added to Drivers, but their record could not be read just now, so their truck was not created. Try again from their profile.`
                        : `${name} added to Drivers. Their truck needs a VIN and an equipment type before it can be created — add it from their profile.`
                )
                setClaiming(false)
                closeClaimModal()
                navigate('/profiles/drivers')
                return
            }
            const settled = await addTruckForClaimedDriver(complete, driverId, name)
            setClaiming(false)
            closeClaimModal()
            // A seat question is not a failure and not a success: leave the operator here to answer it,
            // and let whichever way they answer decide when this flow is over.
            if (settled) navigate('/profiles/drivers')
            return
        }
        showSnackbar(SnackbarType.SUCCESS, `${name} added to Drivers. You can now manage their profile and app access.`)

        setClaiming(false)
        closeClaimModal()
        navigate('/profiles/drivers')
    }

    // Two cohesive halves rather than eight loose values: what the page RENDERS, and what it can DO. The
    // state machine can grow a branch without every consumer growing a destructure.
    return {
        state: { claimTarget, claiming, seatPrompt },
        actions: { startClaim: setClaimTarget, confirmClaim, confirmSeatOverage, dismissSeatPrompt, closeClaimModal },
    }
}
